/* OSPracticeToday — Director-training daily practice.
 * Phase 6: Replaces the old "CEO" tab. Picks a practice card from a
 * deterministic server library (0 LLM calls) based on available minutes
 * and target skills. Save produces NO lead, NO sales side effects.
 */
import React, { useEffect, useState } from "react";
import { Loader2, Sparkles, Target, ListChecks, HelpCircle, Clock, Clapperboard } from "lucide-react";
import { osPost, osGet, t } from "./osApi";

const MIN_OPTIONS = [
    { v: 15, en: "15 min · quick drill", cn: "15 分钟 · 短练" },
    { v: 30, en: "30 min · focused block", cn: "30 分钟 · 专注块" },
    { v: 60, en: "60 min · deep work", cn: "60 分钟 · 深度练习" },
];

export default function OSPracticeToday({ lang }) {
    const [skills, setSkills] = useState([]);
    const [selected, setSelected] = useState([]);
    const [minutes, setMinutes] = useState(30);
    const [scene, setScene] = useState("");
    const [card, setCard] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const r = await osGet("/os/today/skills");
                setSkills(r.data.skills || []);
            } catch (e) { /* keeps page usable */ }
        })();
    }, []);

    const toggle = (k) => setSelected((prev) => prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k].slice(0, 4));

    const generate = async () => {
        setLoading(true); setErr(""); setCard(null);
        try {
            const r = await osPost("/os/today/practice-plan", {
                minutes, skills: selected, scene,
            });
            setCard(r.data);
        } catch (e) {
            setErr(e?.response?.data?.detail || t(lang, "Failed to load practice", "无法加载练习"));
        } finally { setLoading(false); }
    };

    return (
        <div className="max-w-2xl mx-auto space-y-10 pb-10" data-testid="os-practice-today">
            <section>
                <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4A574] font-bold inline-flex items-center gap-1.5">
                    <Clapperboard size={13} /> {t(lang, "Today's practice", "今日练习")}
                </p>
                <h1 className="font-serif text-4xl sm:text-5xl mt-4 leading-tight">
                    {t(lang, "20–30 min that moves me as a director.", "20–30 分钟 · 直接推动导演能力。")}
                </h1>
                <p className="mt-4 text-[13.5px] text-[#F5F0E8]/55 leading-relaxed max-w-xl">
                    {t(lang,
                        "A deterministic practice card — no AI, no credits burned. Pick your window and skills; I'll match a drill from the library with a clear objective, task, finish criterion and one reflection question.",
                        "基于种子库匹配，不调用 AI，不烧预算。选时间 + 想练的能力，自动匹配一条有目标、任务、完成标准、一个复盘问题的练习。")}
                </p>
            </section>

            <section className="rounded-2xl border border-white/10 bg-white/4 p-5 md:p-6 space-y-5">
                <div>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-[#F5F0E8]/50 font-bold mb-2.5 inline-flex items-center gap-1.5">
                        <Clock size={11} /> {t(lang, "Time I have today", "今天我有多少时间")}
                    </p>
                    <div className="flex gap-2 flex-wrap" data-testid="os-practice-minutes">
                        {MIN_OPTIONS.map((o) => (
                            <button key={o.v} data-testid={`os-practice-min-${o.v}`}
                                onClick={() => setMinutes(o.v)}
                                className={`rounded-full px-4 py-2 text-[11.5px] uppercase tracking-[0.14em] font-semibold border transition-colors ${minutes === o.v ? "bg-[#D4A574] text-[#0D0A08] border-[#D4A574]" : "border-white/15 text-[#F5F0E8]/70 hover:border-[#D4A574]"}`}>
                                {t(lang, o.en, o.cn)}
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-[#F5F0E8]/50 font-bold mb-2.5">
                        {t(lang, "Skill focus (pick up to 4)", "想练的能力（最多 4 项）")}
                    </p>
                    <div className="flex gap-2 flex-wrap" data-testid="os-practice-skills">
                        {skills.map((s) => (
                            <button key={s.key} data-testid={`os-practice-skill-${s.key}`}
                                onClick={() => toggle(s.key)}
                                className={`rounded-full px-3.5 py-1.5 text-[11.5px] border transition-colors ${selected.includes(s.key) ? "bg-[#7BA05B] text-[#0D0A08] border-[#7BA05B]" : "border-white/15 text-[#F5F0E8]/65 hover:border-[#7BA05B]"}`}>
                                {t(lang, s.en, s.cn)}
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-[#F5F0E8]/50 font-bold mb-2">
                        {t(lang, "Scene / subject / gear (optional)", "场景 / 主体 / 设备（可选）")}
                    </p>
                    <textarea rows={2} value={scene} data-testid="os-practice-scene"
                        onChange={(e) => setScene(e.target.value)}
                        placeholder={t(lang, "e.g. kopi + kaya toast at a window table, 50mm, iPhone 15 Pro",
                            "例如：窗边咖椰吐司，50mm，iPhone 15 Pro")}
                        className="w-full rounded-xl bg-white/5 border border-white/12 px-4 py-2.5 text-[12.5px] focus:outline-none focus:border-[#D4A574] resize-none" />
                </div>

                <button onClick={generate} disabled={loading} data-testid="os-practice-generate"
                    className="w-full rounded-full bg-[#D4A574] text-[#0D0A08] py-3.5 text-[12px] uppercase tracking-[0.2em] font-bold hover:bg-[#F5F0E8] transition-colors disabled:opacity-40 inline-flex items-center justify-center gap-2">
                    {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                    {t(lang, "Build today's practice", "生成今日练习")}
                </button>
                <p className="text-[10.5px] text-[#F5F0E8]/40 text-center">
                    {t(lang,
                        "Deterministic from the local library · 0 LLM calls · 0 credits.",
                        "来自本地种子库 · 不调用 AI · 不消耗额度。")}
                </p>
                {err && <p className="text-[#C0604A] text-[12px] text-center" data-testid="os-practice-error">{err}</p>}
            </section>

            {card && (
                <section className="rounded-2xl border border-[#D4A574]/40 bg-[#D4A574]/6 p-6 md:p-8 space-y-6" data-testid="os-practice-card">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4A574] font-bold">
                            {t(lang, "Today's drill", "今日练习")}
                        </p>
                        <div className="flex gap-2 flex-wrap">
                            <span className="text-[10px] uppercase tracking-[0.14em] text-[#F5F0E8]/50 border border-white/15 rounded-full px-2.5 py-0.5">{card.est_min} min</span>
                            {(card.tags || []).map((tg) => <span key={tg} className="text-[10px] uppercase tracking-[0.14em] text-[#D4A574] border border-[#D4A574]/40 rounded-full px-2.5 py-0.5">{tg.replace(/_/g, " ")}</span>)}
                        </div>
                    </div>

                    <Block icon={Target} title={t(lang, "Objective", "目标")} body={t(lang, card.objective_en, card.objective_cn)} testid="os-practice-objective" />
                    <Block icon={ListChecks} title={t(lang, "Task", "任务")} body={t(lang, card.task_en, card.task_cn)} testid="os-practice-task" />
                    <Block icon={Sparkles} title={t(lang, "Finish criterion", "完成标准")} body={t(lang, card.criteria_en, card.criteria_cn)} testid="os-practice-criteria" />
                    <Block icon={HelpCircle} title={t(lang, "Reflection", "复盘问题")} body={t(lang, card.reflect_q_en, card.reflect_q_cn)} testid="os-practice-reflect" tone="warm" />

                    {card.scene_note && (
                        <div className="rounded-xl bg-white/5 border border-white/10 p-4">
                            <p className="text-[10px] uppercase tracking-[0.22em] text-[#F5F0E8]/50 font-bold mb-1.5">{t(lang, "Your scene note", "现场记录")}</p>
                            <p className="text-[12.5px] text-[#F5F0E8]/75 whitespace-pre-wrap leading-relaxed">{card.scene_note}</p>
                        </div>
                    )}

                    <p className="text-[10.5px] text-[#F5F0E8]/40 text-right">
                        {t(lang, "Save results in Works · Reflect in Growth.",
                            "练完后：练习作品 保存 · 复盘与成长 回看。")}
                    </p>
                </section>
            )}
        </div>
    );
}

function Block({ icon: Icon, title, body, testid, tone = "cool" }) {
    const color = tone === "warm" ? "#C0604A" : "#F5F0E8";
    return (
        <div data-testid={testid}>
            <p className="text-[10px] uppercase tracking-[0.22em] font-bold mb-1.5 inline-flex items-center gap-1.5" style={{ color }}>
                <Icon size={11} /> {title}
            </p>
            <p className="text-[14px] text-[#F5F0E8]/90 leading-relaxed whitespace-pre-wrap">{body}</p>
        </div>
    );
}
